import { CSSProperties } from 'react';
import { Header } from '@tanstack/react-table';
import { components } from '@atlas-frontend/design-tokens';
import {
  TABLE_HEADER_TEXT_MIN_WIDTH_PX,
  TABLE_HEADER_ACTION_WIDTH_PX,
} from '../common/constants';

const readCssPxValue = (value: CSSProperties['width']) => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string' && value.endsWith('px')) {
    return Number.parseFloat(value);
  }

  return undefined;
};

interface HeaderSizingOptions {
  canSort: boolean;
  canFilter: boolean;
}

interface HeaderMinWidthOptions extends HeaderSizingOptions {
  cellPaddingPx: number;
  configuredMinWidthPx?: number;
}

export const getHeaderActionsWidthPx = ({
  canSort,
  canFilter,
}: HeaderSizingOptions) => {
  const actionsCount = Number(canSort) + Number(canFilter);
  return actionsCount * TABLE_HEADER_ACTION_WIDTH_PX;
};

export const getHeaderMinWidthPx = ({
  canSort,
  canFilter,
  cellPaddingPx,
  configuredMinWidthPx = 0,
}: HeaderMinWidthOptions) =>
  Math.max(
    TABLE_HEADER_TEXT_MIN_WIDTH_PX +
      getHeaderActionsWidthPx({ canSort, canFilter }) +
      cellPaddingPx,
    configuredMinWidthPx,
  );

const getCssMinWidthValue = (
  configuredMinWidth: CSSProperties['minWidth'],
  minWidthPx: number,
): CSSProperties['minWidth'] => {
  if (typeof configuredMinWidth === 'string' && configuredMinWidth.trim()) {
    return `max(${configuredMinWidth}, ${minWidthPx}px)`;
  }

  return minWidthPx;
};

const getCssWidthWithMinConstraint = (
  widthPx: number,
  configuredMinWidth: CSSProperties['minWidth'],
  minWidthPx: number,
): CSSProperties['width'] => {
  if (typeof configuredMinWidth === 'string' && configuredMinWidth.trim()) {
    return `max(${widthPx}px, ${configuredMinWidth}, ${minWidthPx}px)`;
  }

  return Math.max(widthPx, minWidthPx);
};

const getColumnMinWidthPx = (
  header: Header<any, unknown>,
  defaultStyle: CSSProperties,
) => {
  const cellPaddingPx = components.Table.cellPaddingInlineMD * 2;
  const configuredMinWidthPx = readCssPxValue(defaultStyle.minWidth);

  return getHeaderMinWidthPx({
    canSort: header.column.getCanSort(),
    canFilter: header.column.getCanFilter(),
    cellPaddingPx,
    configuredMinWidthPx,
  });
};

export const getHeaderWidthStyle = (
  header: Header<any, unknown>,
  defaultStyle: CSSProperties,
): CSSProperties => {
  const minWidthPx = getColumnMinWidthPx(header, defaultStyle);
  const cssMinWidth = getCssMinWidthValue(defaultStyle.minWidth, minWidthPx);
  const tableState = header.getContext().table.getState();
  const configuredWidthPx = readCssPxValue(defaultStyle.width);

  return tableState.columnSizingInfo.isInitialized
    ? {
        ...defaultStyle,
        minWidth: cssMinWidth,
        width: getCssWidthWithMinConstraint(
          header.getSize(),
          defaultStyle.minWidth,
          minWidthPx,
        ),
      }
    : {
        ...defaultStyle,
        minWidth: cssMinWidth,
        width:
          typeof configuredWidthPx === 'number'
            ? getCssWidthWithMinConstraint(
                configuredWidthPx,
                defaultStyle.minWidth,
                minWidthPx,
              )
            : defaultStyle.width,
      };
};
